import { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import PageHeader from '@/components/common/PageHeader';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { useDebouncedValue } from '@/hooks/ui/useDebouncedValue';
import { cn } from '@/lib/utils';
import { Check, ChevronsUpDown } from 'lucide-react';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from '@/components/ui/pagination';
import { ProductService } from '@/services/marketplace/ProductService';
import { BrandService } from '@/services/marketplace/BrandService';
import { MarketplaceService } from '@/services/marketplace/MarketplaceService';
import { ProductImageService } from '@/services/marketplace/ProductImageService';
import { Search, Filter, ExternalLink, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const PRODUCTS_PER_PAGE = 20;

const MarketplaceAdmin = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10) || 1);
  const selectedCategory = searchParams.get('category') ?? 'all';
  const selectedBrand = searchParams.get('brand') ?? 'all';
  const searchTerm = searchParams.get('search') ?? '';
  const hasGeniesFilter = searchParams.get('genies') ?? 'all';

  const [generatingImageFor, setGeneratingImageFor] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<{ url: string; name: string } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, value]) => {
          if (value === '' || value === 'all') next.delete(key);
          else next.set(key, value);
        });
        return next;
      });
    },
    [setSearchParams]
  );

  const { data: productsResult, isLoading: productsLoading } = useQuery({
    queryKey: ['admin-marketplace-products', searchTerm, selectedCategory, selectedBrand, hasGeniesFilter, page],
    queryFn: async () => {
      const filterPayload: any = {};
      if (searchTerm) filterPayload.search = searchTerm;
      if (selectedCategory !== 'all') filterPayload.category_id = selectedCategory;
      if (selectedBrand !== 'all') filterPayload.brand_id = selectedBrand;
      if (hasGeniesFilter === 'with') filterPayload.has_genies = true;
      return ProductService.getProductsWithPagination(filterPayload, page, PRODUCTS_PER_PAGE);
    },
  });

  const products = productsResult?.data ?? [];
  const pagination = productsResult?.pagination ?? {
    page: 1,
    limit: PRODUCTS_PER_PAGE,
    total: 0,
    totalPages: 0,
    hasMore: false,
  };

  // Preload recent categories and brands for combobox initial options
  const { data: initialCategories = [] } = useQuery({
    queryKey: ['admin-marketplace-categories-initial'],
    queryFn: () => MarketplaceService.listCategoriesRecent(20),
  });

  const { data: initialBrands = [] } = useQuery({
    queryKey: ['admin-marketplace-brands-initial'],
    queryFn: () => BrandService.listBrandsRecent(20),
  });

  const [categoryOpen, setCategoryOpen] = useState(false);
  const [categorySearchInput, setCategorySearchInput] = useState('');
  const [categoryOptions, setCategoryOptions] = useState<typeof initialCategories>(initialCategories);
  const debouncedCategorySearch = useDebouncedValue(categorySearchInput, 300);

  const [brandOpen, setBrandOpen] = useState(false);
  const [brandSearchInput, setBrandSearchInput] = useState('');
  const [brandOptions, setBrandOptions] = useState<typeof initialBrands>(initialBrands);
  const debouncedBrandSearch = useDebouncedValue(brandSearchInput, 300);

  useEffect(() => {
    setCategoryOptions(initialCategories);
  }, [initialCategories]);

  useEffect(() => {
    if (!debouncedCategorySearch.trim()) {
      setCategoryOptions(initialCategories);
      return;
    }
    MarketplaceService.searchCategories(debouncedCategorySearch, 20).then(setCategoryOptions);
  }, [debouncedCategorySearch, initialCategories]);

  useEffect(() => {
    setBrandOptions(initialBrands);
  }, [initialBrands]);

  useEffect(() => {
    if (!debouncedBrandSearch.trim()) {
      setBrandOptions(initialBrands);
      return;
    }
    BrandService.searchBrands(debouncedBrandSearch, 20).then(setBrandOptions);
  }, [debouncedBrandSearch, initialBrands]);

  const { data: selectedCategoryData } = useQuery({
    queryKey: ['admin-marketplace-category-selected', selectedCategory],
    queryFn: () => MarketplaceService.getCategoryById(selectedCategory),
    enabled: selectedCategory !== 'all' && !!selectedCategory,
  });

  const { data: selectedBrandData } = useQuery({
    queryKey: ['admin-marketplace-brand-selected', selectedBrand],
    queryFn: () => BrandService.getBrandById(selectedBrand),
    enabled: selectedBrand !== 'all' && !!selectedBrand,
  });

  const categoryLabel = selectedCategory === 'all' ? 'All Categories' : (selectedCategoryData?.name ?? selectedCategory);
  const brandLabel = selectedBrand === 'all' ? 'All Brands' : (selectedBrandData?.name ?? selectedBrand);

  const handleResetFilters = () => {
    updateParams({ page: '1', search: '', category: 'all', brand: 'all', genies: 'all' });
  };

  const handleGenerateImage = async (product: any) => {
    setGeneratingImageFor(product.id);
    toast.info(`Generating image for ${product.name}...`, {
      description: 'This may take 10-30 seconds'
    });

    try {
      const [brand, category] = await Promise.all([
        BrandService.getBrandById(product.brand_id),
        product.category_id ? MarketplaceService.getCategoryById(product.category_id) : Promise.resolve(null),
      ]);

      const imageUrl = await ProductImageService.generateImageForProduct(
        product,
        brand,
        category?.slug
      );

      toast.success('Image generated successfully!', {
        description: 'Product record has been updated'
      });

      // Refetch products to show the new image
      // Note: Using window.location.reload() is simple but you could also
      // use queryClient.invalidateQueries(['admin-marketplace-products'])
      window.location.reload();
    } catch (error) {
      console.error('Error generating image:', error);
      toast.error('Failed to generate image', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setGeneratingImageFor(null);
    }
  };

  const filteredProducts = products;
  const { total, totalPages } = pagination;
  const startItem = total === 0 ? 0 : (page - 1) * PRODUCTS_PER_PAGE + 1;
  const endItem = Math.min(page * PRODUCTS_PER_PAGE, total);

  // #region agent log
  useEffect(() => {
    if (productsLoading || filteredProducts.length === 0) return;
    const t = setTimeout(() => {
      requestAnimationFrame(() => {
        const root = rootRef.current;
        const main = root?.parentElement;
        const scrollArea = main?.parentElement;
        const flexRow = scrollArea?.parentElement;
        fetch('http://127.0.0.1:7242/ingest/518058b3-d4b4-4554-8ee1-afb67e98aaad', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: 'MarketplaceAdmin.tsx:products-loaded', message: 'dimensions when products loaded', data: { rootOffset: root?.offsetHeight, rootScroll: root?.scrollHeight, mainOffset: main?.offsetHeight, scrollAreaOffset: scrollArea?.offsetHeight, scrollAreaScroll: scrollArea?.scrollHeight, flexRowOffset: flexRow?.offsetHeight, innerHeight: window.innerHeight, bodyScroll: document.body.scrollHeight, docScroll: document.documentElement.scrollHeight, productCount: filteredProducts.length }, timestamp: Date.now(), sessionId: 'debug-session', runId: 'post-fix', hypothesisId: 'H1' }) }).catch(() => { });
      });
    }, 100);
    return () => clearTimeout(t);
  }, [productsLoading, filteredProducts.length]);
  // #endregion

  useEffect(() => {
    document.documentElement.classList.add('marketplace-admin-no-doc-scroll');
    return () => document.documentElement.classList.remove('marketplace-admin-no-doc-scroll');
  }, []);

  return (
    <div ref={rootRef} className="min-h-screen bg-[#F9FAFC]">
      <div className="container mx-auto px-4 py-8 max-w-[1920px]">
        <PageHeader title="Marketplace Management" genieIcon="globe" />

        {/* <div className="mb-4">
          <Link
            to="/admin"
            className="text-sm px-3 py-1.5 rounded-lg bg-genie-primary/10 text-genie-primary hover:bg-genie-primary/20 inline-block"
          >
            Back to Admin Operations
          </Link>
        </div> */}

        <div className="bg-white border border-genie-border rounded-xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-genie-primary" />
            <h2 className="text-lg font-semibold">Filters</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => updateParams({ search: e.target.value, page: '1' })}
                className="pl-10"
              />
            </div>

            {/* Category Filter - Combobox */}

            <Popover open={categoryOpen} onOpenChange={(open) => { setCategoryOpen(open); if (!open) setCategorySearchInput(''); }}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={categoryOpen}
                  className="w-full justify-between font-normal"
                >
                  <span className="truncate">{categoryLabel}</span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                <Command filter={() => 1}>
                  <CommandInput
                    placeholder="Search categories..."
                    value={categorySearchInput}
                    onValueChange={setCategorySearchInput}
                  />
                  <CommandList>
                    <CommandEmpty>No category found.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="all"
                        onSelect={() => {
                          updateParams({ category: 'all', page: '1' });
                          setCategoryOpen(false);
                        }}
                      >
                        <Check className={cn('mr-2 h-4 w-4', selectedCategory === 'all' ? 'opacity-100' : 'opacity-0')} />
                        All Categories
                      </CommandItem>
                      {categoryOptions.map((c) => (
                        <CommandItem
                          key={c.id}
                          value={c.id}
                          onSelect={() => {
                            updateParams({ category: c.id, page: '1' });
                            setCategoryOpen(false);
                          }}
                        >
                          <Check className={cn('mr-2 h-4 w-4', selectedCategory === c.id ? 'opacity-100' : 'opacity-0')} />
                          {c.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            {/* Brand Filter - Combobox */}
            <Popover open={brandOpen} onOpenChange={(open) => { setBrandOpen(open); if (!open) setBrandSearchInput(''); }}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={brandOpen}
                  className="w-full justify-between font-normal"
                >
                  <span className="truncate">{brandLabel}</span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                <Command filter={() => 1}>
                  <CommandInput
                    placeholder="Search brands..."
                    value={brandSearchInput}
                    onValueChange={setBrandSearchInput}
                  />
                  <CommandList>
                    <CommandEmpty>No brand found.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="all"
                        onSelect={() => {
                          updateParams({ brand: 'all', page: '1' });
                          setBrandOpen(false);
                        }}
                      >
                        <Check className={cn('mr-2 h-4 w-4', selectedBrand === 'all' ? 'opacity-100' : 'opacity-0')} />
                        All Brands
                      </CommandItem>
                      {brandOptions.map((b) => (
                        <CommandItem
                          key={b.id}
                          value={b.id}
                          onSelect={() => {
                            updateParams({ brand: b.id, page: '1' });
                            setBrandOpen(false);
                          }}
                        >
                          <Check className={cn('mr-2 h-4 w-4', selectedBrand === b.id ? 'opacity-100' : 'opacity-0')} />
                          {b.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            {/* Has Genies Filter */}
            <Select
              value={hasGeniesFilter}
              onValueChange={(value) => updateParams({ genies: value, page: '1' })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Genie Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Products</SelectItem>
                <SelectItem value="with">With Genies</SelectItem>
                <SelectItem value="without">Without Genies</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {total === 0
                ? 'No products'
                : `Showing ${startItem}–${endItem} of ${total} product${total !== 1 ? 's' : ''}`}
            </div>
            <Button variant="outline" size="sm" onClick={handleResetFilters}>
              Reset Filters
            </Button>
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-white border border-genie-border rounded-xl overflow-hidden">
          {productsLoading ? (
            <div className="p-8 text-center text-gray-500">Loading products...</div>
          ) : filteredProducts.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No products found</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]">#</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Brand</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Model Number</TableHead>
                    <TableHead className="text-center">Genies</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProducts.map((product, index) => (
                    <TableRow key={product.id}>
                      <TableCell className="text-gray-500">
                        {(page - 1) * PRODUCTS_PER_PAGE + index + 1}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {product.image_url ? (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleGenerateImage(product)}
                                disabled={generatingImageFor === product.id}
                                className="p-1.5 rounded hover:bg-purple-50 text-purple-600 hover:text-purple-700 transition-colors disabled:opacity-50"
                                title="Regenerate image"
                              >
                                {generatingImageFor === product.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Sparkles className="w-4 h-4" />
                                )}
                              </button>
                              <div className="relative">
                                <img
                                  src={product.image_url}
                                  alt={product.name}
                                  onClick={() => setPreviewImage({ url: product.image_url, name: product.name })}
                                  className="w-16 h-16 object-cover rounded border-2 border-purple-200 cursor-pointer hover:border-purple-400 transition-colors"
                                />
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleGenerateImage(product)}
                              disabled={generatingImageFor === product.id}
                              className="w-16 h-16 bg-gradient-to-br from-purple-100 to-blue-100 rounded border-2 border-dashed border-gray-300 hover:border-purple-400 hover:from-purple-200 hover:to-blue-200 transition-all flex flex-col items-center justify-center gap-1 group"
                            >
                              {generatingImageFor === product.id ? (
                                <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
                              ) : (
                                <>
                                  <Sparkles className="w-5 h-5 text-purple-600 group-hover:text-purple-700" />
                                  <span className="text-[9px] font-medium text-purple-600 group-hover:text-purple-700">Generate</span>
                                </>
                              )}
                            </button>
                          )}
                          <div>
                            <div className="font-medium">{product.name}</div>
                            {product.description && (
                              <div className="text-xs text-gray-500 line-clamp-1">
                                {product.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {product.brand_logo_url && (
                            <img
                              src={product.brand_logo_url || product?.marketplace_brands?.logo_url}
                              alt={product.brand_name || product?.marketplace_brands?.name}
                              className="w-6 h-6 object-contain"
                            />
                          )}
                          <span>{product.brand_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>{product.category_name || product?.marketplace_categories?.name}</TableCell>
                      <TableCell>
                        {product.model_number ? (
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                            {product.model_number}
                          </code>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {product.genie_count > 0 ? (
                          <Badge variant="default" className="bg-genie-primary">
                            {product.genie_count}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-gray-400">
                            0
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {product.genie_count > 0 ? (
                          <Badge variant="default" className="bg-green-500">
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-orange-500 border-orange-500">
                            No Genie
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {/* <Link
                          to={`/marketplace/brands/${product.brand_slug}/products/${product.slug}`}
                          target="_blank"
                          className="inline-flex items-center gap-1 text-genie-primary hover:underline text-sm"
                        >
                          View
                          <ExternalLink className="w-3 h-3" />
                        </Link> */}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {!productsLoading && totalPages > 1 && (
            <div className="flex items-center justify-center border-t px-4 py-3">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (page > 1) updateParams({ page: String(page - 1) });
                      }}
                      className={page <= 1 ? 'pointer-events-none opacity-50' : ''}
                      aria-disabled={page <= 1}
                    />
                  </PaginationItem>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => {
                      if (totalPages <= 7) return true;
                      if (p === 1 || p === totalPages) return true;
                      if (Math.abs(p - page) <= 1) return true;
                      return false;
                    })
                    .reduce<number[]>((acc, p, i, arr) => {
                      if (i > 0 && arr[i - 1] !== p - 1) acc.push(-1);
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((p, i) =>
                      p === -1 ? (
                        <PaginationItem key={`ellipsis-${i}`}>
                          <PaginationEllipsis />
                        </PaginationItem>
                      ) : (
                        <PaginationItem key={p}>
                          <PaginationLink
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              updateParams({ page: String(p) });
                            }}
                            isActive={page === p}
                          >
                            {p}
                          </PaginationLink>
                        </PaginationItem>
                      )
                    )}
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (page < totalPages) updateParams({ page: String(page + 1) });
                      }}
                      className={page >= totalPages ? 'pointer-events-none opacity-50' : ''}
                      aria-disabled={page >= totalPages}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </div>
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] bg-white rounded-lg overflow-hidden">
            {/* Header */}
            <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/60 to-transparent p-4 z-10">
              <div className="flex items-center justify-between text-white">
                <h3 className="font-semibold text-lg">{previewImage.name}</h3>
                <button
                  onClick={() => setPreviewImage(null)}
                  className="hover:bg-white/20 rounded-full p-2 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Image */}
            <img
              src={previewImage.url}
              alt={previewImage.name}
              className="max-w-full max-h-[90vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Footer */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
              <div className="flex items-center justify-end gap-2">
                <a
                  href={previewImage.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-white/90 hover:bg-white text-gray-900 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="w-4 h-4" />
                  Open in New Tab
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketplaceAdmin;
